import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  FiUsers, FiDownload, FiSearch, FiFilter, 
  FiFileText, FiPhone, FiCalendar,
  FiChevronLeft, FiChevronRight, FiRefreshCw, FiShield
} from 'react-icons/fi';
import AdminLogin from '../components/AdminLogin';
import api from '../services/api';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [enrollments, setEnrollments] = useState([]);
  const [filteredEnrollments, setFilteredEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStream, setFilterStream] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [stats, setStats] = useState({
    total: 0,
    tech: 0,
    nonTech: 0,
    today: 0,
    revenue: 0
  });

  useEffect(() => {
    const savedAuth = window.sessionStorage.getItem('dylora_admin_auth');
    if (savedAuth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchEnrollments();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    filterEnrollments();
  }, [searchTerm, filterStream, enrollments]);

  const fetchEnrollments = async () => {
    setLoading(true);
    try {
      // Fetch real data from backend
      const response = await api.getAllEnrollments();
      console.log('📊 Enrollments fetched:', response.data);
      
      setEnrollments(response.data || []);
      calculateStats(response.data || []);
    } catch (error) {
      console.error('Error fetching enrollments:', error);
      // Fallback to empty array
      setEnrollments([]);
      calculateStats([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    const techStreams = ['CS', 'IT', 'DSDA', 'AI'];
    const today = new Date().toDateString();
    
    const newStats = {
      total: data.length,
      tech: data.filter(e => techStreams.includes(e.stream)).length,
      nonTech: data.filter(e => !techStreams.includes(e.stream)).length,
      today: data.filter(e => {
        const enrollmentDate = new Date(e.enrolledAt || e.createdAt).toDateString();
        return enrollmentDate === today;
      }).length,
      revenue: data.reduce((sum, e) => sum + (e.amount || 0), 0)
    };
    
    setStats(newStats);
  };

  const filterEnrollments = () => {
    let filtered = [...enrollments];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(e =>
        (e.name && e.name.toLowerCase().includes(term)) ||
        (e.email && e.email.toLowerCase().includes(term)) ||
        (e.phone && e.phone.toString().includes(term)) ||
        (e.college && e.college.toLowerCase().includes(term))
      );
    }

    if (filterStream !== 'all') {
      if (filterStream === 'tech') {
        filtered = filtered.filter(e => ['CS', 'IT', 'DSDA', 'AI'].includes(e.stream));
      } else if (filterStream === 'non-tech') {
        filtered = filtered.filter(e => !['CS', 'IT', 'DSDA', 'AI'].includes(e.stream));
      } else {
        filtered = filtered.filter(e => e.stream === filterStream);
      }
    }

    setFilteredEnrollments(filtered);
    setCurrentPage(1);
  };

  const exportToCSV = () => {
    if (filteredEnrollments.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = ['Name', 'Email', 'Phone', 'College', 'Stream', 'Domain', 'Passing Year', 'Amount', 'Payment Status', 'Registered Date', 'WhatsApp Group'];
    
    const csvData = filteredEnrollments.map(e => [
      e.name || 'N/A',
      e.email || 'N/A',
      e.phone || 'N/A',
      e.college || 'N/A',
      e.stream || 'N/A',
      e.domainName || e.interest || 'N/A',
      e.passingYear || 'N/A',
      `₹${e.amount || 0}`,
      e.paymentStatus || 'pending',
      new Date(e.enrolledAt || e.createdAt).toLocaleDateString(),
      e.whatsappGroup || 'N/A'
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `enrollments_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleLogin = () => {
    window.sessionStorage.setItem('dylora_admin_auth', 'true');
    setIsAuthenticated(true);
  };
  const handleLogout = () => {
    window.sessionStorage.removeItem('dylora_admin_auth');
    setIsAuthenticated(false);
    setEnrollments([]);
    setFilteredEnrollments([]);
  };

  const streamOptions = [
    { value: 'all', label: 'All Streams' },
    { value: 'tech', label: 'Tech Streams' },
    { value: 'non-tech', label: 'Non-Tech Streams' },
    { value: 'CS', label: 'Computer Science' },
    { value: 'IT', label: 'Information Technology' },
    { value: 'DSDA', label: 'Data Science' },
    { value: 'BA', label: 'Business Administration' },
    { value: 'BCOM', label: 'Commerce' },
    { value: 'BMMC', label: 'Mass Media' },
  ];

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredEnrollments.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredEnrollments.length / itemsPerPage);

  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <div className="admin-logo">
              <span className="logo-text">DYLORA</span>
              <span className="admin-badge">ADMIN</span>
            </div>
            <h1 className="dashboard-title">Enrollment Dashboard</h1>
            <div className="header-stats">
              <span className="stat-badge">Total: {stats.total}</span>
              <span className="stat-badge">Today: {stats.today}</span>
              <span className="stat-badge">Revenue: ₹{stats.revenue}</span>
            </div>
          </div>
          <div className="header-right">
            <button
              onClick={() => navigate('/dipadminv/certificates')}
              className="refresh-header-button black-btn-outline"
            >
              <FiShield className="icon" />
              Certificates
            </button>
            <button onClick={fetchEnrollments} className="refresh-header-button black-btn-outline">
              <FiRefreshCw className="icon" />
              Refresh
            </button>
            <button onClick={handleLogout} className="logout-button black-btn-outline">
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="stats-grid">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="stat-card">
          <div className="stat-icon"><FiUsers size={28} /></div>
          <div className="stat-content">
            <div className="stat-number">{stats.total}</div>
            <div className="stat-label">Total Enrollments</div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }} className="stat-card tech-stat">
          <div className="stat-icon"><FiUsers size={28} /></div>
          <div className="stat-content">
            <div className="stat-number">{stats.tech}</div>
            <div className="stat-label">Tech Students</div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.2 }} className="stat-card">
          <div className="stat-icon"><FiUsers size={28} /></div>
          <div className="stat-content">
            <div className="stat-number">{stats.nonTech}</div>
            <div className="stat-label">Non-Tech Students</div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.3 }} className="stat-card">
          <div className="stat-icon"><FiCalendar size={28} /></div>
          <div className="stat-content">
            <div className="stat-number">{stats.today}</div>
            <div className="stat-label">Today's Enrollments</div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.4 }} className="stat-card revenue-stat">
          <div className="stat-icon"><FiFileText size={28} /></div>
          <div className="stat-content">
            <div className="stat-number">₹{stats.revenue}</div>
            <div className="stat-label">Total Revenue</div>
          </div>
        </motion.div>
      </div>

      {/* Controls */}
      <div className="controls">
        <div className="search-container">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by name, email, phone, or college..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-container">
          <FiFilter className="filter-icon" />
          <select
            value={filterStream}
            onChange={(e) => setFilterStream(e.target.value)}
            className="filter-select"
          >
            {streamOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <button onClick={fetchEnrollments} className="refresh-button black-btn-outline">
          <FiRefreshCw className="icon" />
          Refresh Data
        </button>

        <button onClick={exportToCSV} className="export-button black-btn">
          <FiDownload className="icon" />
          Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
            Loading enrollments...
          </div>
        ) : filteredEnrollments.length === 0 ? (
          <div className="empty-state">
            {enrollments.length === 0 ? (
              <>
                <div className="empty-icon">📊</div>
                <h3>No enrollments found</h3>
                <p>No registrations have been made yet.</p>
                <button onClick={fetchEnrollments} className="retry-button black-btn">
                  Check Again
                </button>
              </>
            ) : (
              <>
                <div className="empty-icon">🔍</div>
                <h3>No matching enrollments</h3>
                <p>Try changing your search or filter criteria.</p>
                <button onClick={() => {setSearchTerm(''); setFilterStream('all');}} className="retry-button black-btn-outline">
                  Clear Filters
                </button>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="table-header">
              <div className="table-info">
                Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredEnrollments.length)} of {filteredEnrollments.length} enrollments
              </div>
              <div className="table-actions">
                <button onClick={exportToCSV} className="table-action-button">
                  <FiDownload className="icon" />
                  Export
                </button>
              </div>
            </div>

            <div className="table-wrapper">
              <table className="enrollments-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Contact</th>
                    <th>College</th>
                    <th>Stream</th>
                    <th>Domain</th>
                    <th>Year</th>
                    <th>Payment</th>
                    <th>Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((enrollment, index) => (
                    <tr key={enrollment._id || enrollment.id || index}>
                      <td>
                        <div className="name-cell">
                          <div className="name">{enrollment.name || 'N/A'}</div>
                          <div className="email">{enrollment.email || 'N/A'}</div>
                        </div>
                      </td>
                      <td>
                        <div className="contact-cell">
                          <div className="phone">
                            <FiPhone className="contact-icon" />
                            {enrollment.phone || 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td>{enrollment.college || 'N/A'}</td>
                      <td>
                        <span className={`stream-badge ${['CS', 'IT', 'DSDA', 'AI'].includes(enrollment.stream) ? 'tech' : 'non-tech'}`}>
                          {enrollment.stream || 'N/A'}
                        </span>
                      </td>
                      <td>
                        {enrollment.domainName || enrollment.interest || 'N/A'}
                        {enrollment.otherInterest && ` (${enrollment.otherInterest})`}
                      </td>
                      <td>{enrollment.passingYear || 'N/A'}</td>
                      <td>
                        <div className="payment-cell">
                          <div className="amount">₹{enrollment.amount || 0}</div>
                          <div className={`payment-status ${enrollment.paymentStatus}`}>
                            {enrollment.paymentStatus || 'pending'}
                          </div>
                        </div>
                      </td>
                      <td>
                        {enrollment.enrolledAt ? new Date(enrollment.enrolledAt).toLocaleDateString() : 
                         enrollment.createdAt ? new Date(enrollment.createdAt).toLocaleDateString() : 'N/A'}
                      </td>
                      <td>
                        <span className={`status-badge ${enrollment.status}`}>
                          {enrollment.status || 'registered'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="page-button"
                >
                  <FiChevronLeft />
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNumber;
                  if (totalPages <= 5) pageNumber = i + 1;
                  else if (currentPage <= 3) pageNumber = i + 1;
                  else if (currentPage >= totalPages - 2) pageNumber = totalPages - 4 + i;
                  else pageNumber = currentPage - 2 + i;

                  return (
                    <button
                      key={pageNumber}
                      onClick={() => setCurrentPage(pageNumber)}
                      className={`page-button ${currentPage === pageNumber ? 'active' : ''}`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}

                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="page-button"
                >
                  <FiChevronRight />
                </button>

                <div className="page-info">
                  Page {currentPage} of {totalPages}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Summary */}
      <div className="summary">
        <div className="summary-card">
          <h3 className="summary-title">Quick Actions</h3>
          <div className="export-options">
            <button onClick={exportToCSV} className="export-option">
              <FiFileText className="icon" />
              <span>Export to CSV</span>
            </button>
            <button onClick={() => window.print()} className="export-option">
              <FiFileText className="icon" />
              <span>Print Report</span>
            </button>
            <button onClick={fetchEnrollments} className="export-option">
              <FiRefreshCw className="icon" />
              <span>Refresh Data</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
